/** Google / OSM térkép — eladó cím, város, megye. */

const COUNTY_CENTROIDS = {
  Budapest: { lat: 47.4979, lon: 19.0402, zoom: 11 },
  Baranya: { lat: 46.07, lon: 18.23, zoom: 9 },
  "Bács-Kiskun": { lat: 46.56, lon: 19.42, zoom: 9 },
  Békés: { lat: 46.73, lon: 21.09, zoom: 9 },
  "Borsod-Abaúj-Zemplén": { lat: 48.29, lon: 20.59, zoom: 9 },
  "Csongrád-Csanád": { lat: 46.58, lon: 20.18, zoom: 9 },
  Csongrád: { lat: 46.58, lon: 20.18, zoom: 9 },
  Fejér: { lat: 47.19, lon: 18.41, zoom: 9 },
  "Győr-Moson-Sopron": { lat: 47.65, lon: 17.25, zoom: 9 },
  "Hajdú-Bihar": { lat: 47.31, lon: 21.63, zoom: 9 },
  Heves: { lat: 47.88, lon: 20.0, zoom: 9 },
  "Jász-Nagykun-Szolnok": { lat: 47.25, lon: 20.52, zoom: 9 },
  "Komárom-Esztergom": { lat: 47.58, lon: 18.39, zoom: 9 },
  Nógrád: { lat: 48.0, lon: 19.7, zoom: 9 },
  Pest: { lat: 47.45, lon: 19.55, zoom: 9 },
  Somogy: { lat: 46.36, lon: 17.58, zoom: 9 },
  "Szabolcs-Szatmár-Bereg": { lat: 48.01, lon: 22.37, zoom: 9 },
  Tolna: { lat: 46.42, lon: 18.75, zoom: 9 },
  Vas: { lat: 47.01, lon: 16.83, zoom: 9 },
  Veszprém: { lat: 47.1, lon: 17.55, zoom: 9 },
  Zala: { lat: 46.73, lon: 16.95, zoom: 9 },
};

/** Nagyobb városok — finomabb középpont (WGS84). */
const CITY_COORDS = {
  Budapest: { lat: 47.4979, lon: 19.0402, zoom: 12 },
  Debrecen: { lat: 47.5316, lon: 21.6273, zoom: 12 },
  Szeged: { lat: 46.253, lon: 20.1414, zoom: 12 },
  Miskolc: { lat: 48.1035, lon: 20.7784, zoom: 12 },
  Pécs: { lat: 46.0727, lon: 18.2323, zoom: 12 },
  Győr: { lat: 47.6875, lon: 17.6504, zoom: 12 },
  Nyíregyháza: { lat: 47.9554, lon: 21.7167, zoom: 12 },
  Kecskemét: { lat: 46.8964, lon: 19.6897, zoom: 12 },
  Székesfehérvár: { lat: 47.186, lon: 18.422, zoom: 12 },
  Szombathely: { lat: 47.2307, lon: 16.6218, zoom: 12 },
  Érd: { lat: 47.391, lon: 18.911, zoom: 12 },
  Veszprém: { lat: 47.093, lon: 17.911, zoom: 12 },
};

const HUNGARY_OVERVIEW = { lat: 47.1625, lon: 19.5033, zoom: 7 };

function normKey(s) {
  return (s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function lookupCoords(map, name) {
  if (!name) return null;
  const exact = map[name.trim()];
  if (exact) return { ...exact };
  const key = normKey(name);
  for (const [k, v] of Object.entries(map)) {
    if (normKey(k) === key) return { ...v };
  }
  return null;
}

/** Közelítő térkép középpont — cím esetén zoomolunk közelebb. */
export function listingMapCoords(car) {
  if (!car) return null;
  const address = car.address?.trim();
  const city = car.city?.trim();
  const county = car.county?.trim();

  let base = lookupCoords(CITY_COORDS, city) || lookupCoords(COUNTY_CENTROIDS, county);
  if (!base && !city && !county && !address) return null;
  if (!base) base = { ...HUNGARY_OVERVIEW };

  if (address) {
    base = { ...base, zoom: Math.min(14, (base.zoom || 10) + 2) };
  } else if (city) {
    base = { ...base, zoom: Math.max(base.zoom || 11, 11) };
  }
  return base;
}

export function listingMapQuery(car) {
  if (!car) return "";
  const parts = [];
  const address = car.address?.trim();
  const city = car.city?.trim();
  const county = car.county?.trim();
  if (address) parts.push(address);
  if (city) parts.push(city);
  if (county) parts.push(county);
  parts.push("Magyarország");
  return parts.join(", ");
}

/** Megjelenített helyszín sor (cím elsődleges). */
export function listingLocationLabel(car) {
  if (!car) return "";
  const address = car.address?.trim();
  const city = car.city?.trim();
  const county = car.county?.trim();
  if (address) {
    return [address, city, county].filter(Boolean).join(", ");
  }
  return [city, county].filter(Boolean).join(", ");
}

export function googleMapsSearchUrl(query) {
  if (!query) return "https://www.google.com/maps";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function googleMapsEmbedUrl(query, apiKey) {
  if (!query || !apiKey) return null;
  const params = new URLSearchParams({
    key: apiKey,
    q: query,
    language: "hu",
    region: "HU",
  });
  return `https://www.google.com/maps/embed/v1/place?${params.toString()}`;
}

/** OpenStreetMap beágyazás — API kulcs nélkül, Magyarország régió. */
export function openStreetMapEmbedUrl(coords) {
  if (!coords?.lat || !coords?.lon) return null;
  const { lat, lon, zoom = 11 } = coords;
  const scale = 0.22 / Math.max(6, Math.min(14, zoom));
  const dLat = scale * 0.72;
  const dLon = scale;
  const bbox = [lon - dLon, lat - dLat, lon + dLon, lat + dLat].map((n) => n.toFixed(5)).join(",");
  const params = new URLSearchParams({
    bbox,
    layer: "mapnik",
    marker: `${lat},${lon}`,
  });
  return `https://www.openstreetmap.org/export/embed.html?${params.toString()}`;
}

export function listingMapEmbedSrc(car, googleApiKey) {
  const query = listingMapQuery(car);
  const google = googleMapsEmbedUrl(query, googleApiKey);
  if (google) return google;
  const coords = listingMapCoords(car);
  return openStreetMapEmbedUrl(coords);
}

export function openGoogleMaps(query) {
  const url = googleMapsSearchUrl(query);
  window.open(url, "_blank", "noopener,noreferrer");
}
